/**
 * SmartBid Playbook
 * -----------------------------------------------------------------------------
 * RFP Evaluation & Proposal Intelligence guidance. SmartBid is a consultant-led
 * RFP evaluation and proposal intelligence solution — NOT a transformation
 * evaluation/roadmap solution (that's SmartEvaluate). Shares the standard
 * playbook template (Section/Bullets/Callout/Pill/flow strip). Content only.
 */

const VALID = Array.from({ length: 14 }, (_, i) => String(i + 1));

export default function SmartBidPlaybookPage() {
  return (
    <div data-testid="smartbid-playbook-page" className="max-w-4xl">
      <div className="mb-6">
        <p className="text-[10px] uppercase tracking-wider text-[#025979] font-semibold mb-1">
          SmartBid Playbook
        </p>
        <h1 className="text-xl font-bold text-[#111827]" style={{ fontFamily: "Chivo" }}>
          RFP Evaluation &amp; Proposal Intelligence Guidance
        </h1>
        <p className="text-xs text-[#6B7280] mt-1 leading-relaxed max-w-3xl">
          How to use SmartBid throughout RFP reviews, proposal preparation, scope
          assessments, bid qualification, estimation workshops, and customer
          pursuit activities. SmartBid is a consultant-led RFP evaluation and
          proposal intelligence solution.
        </p>
      </div>

      {/* Engagement flow */}
      <div className="bg-[#F8FAFC] border border-[#E5E7EB] rounded-sm p-3 mb-6" data-testid="smartbid-playbook-flow">
        <p className="text-[10px] uppercase tracking-wider text-[#6B7280] font-semibold mb-2">Engagement flow</p>
        <div className="flex flex-wrap items-center gap-2 text-[11px] text-[#111827]">
          <Pill>Receive</Pill><Arrow />
          <Pill>Analyze</Pill><Arrow />
          <Pill>Review</Pill><Arrow />
          <Pill>Estimate</Pill><Arrow />
          <Pill>Recommend</Pill><Arrow />
          <Pill>Submit</Pill>
        </div>
      </div>

      <Section n="1" title="Purpose of SmartBid">
        <Bullets items={[
          "SmartBid accelerates RFP analysis and proposal preparation — it is proposal intelligence, not transformation planning.",
          "Use it to extract and structure requirements from RFP documents quickly and consistently.",
          "Support the bid team with requirement insight, assumptions, risks, and estimation inputs.",
          "Accelerate the pursuit while keeping the consultant accountable for the final proposal.",
        ]} />
      </Section>

      <Section n="2" title="RFP Intake & Qualification">
        <Callout tone="blue">
          Qualify before you commit effort. A disciplined Bid / No-Bid decision protects the team&apos;s capacity.
        </Callout>
        <Bullets items={[
          "Assess RFP completeness — flag missing sections before analysis begins.",
          "Capture submission deadlines and mandatory milestones up front.",
          "Run a clear Bid / No-Bid evaluation rather than defaulting to respond.",
          "Check strategic alignment, customer fit, and resource availability for delivery.",
        ]} />
      </Section>

      <Section n="3" title="Requirement Analysis">
        <Callout tone="amber">
          PRIVA extracts requirements; the consultant validates them. Never treat extraction as final truth.
        </Callout>
        <Bullets items={[
          "Separate functional from technical requirements and confirm each against the source RFP.",
          "Identify integrations, reporting, security, infrastructure, and support requirements explicitly.",
          "Validate every major requirement — consultant review is mandatory, not optional.",
          "Distinguish mandatory requirements from desirable ones so scope and effort are accurate.",
        ]} />
      </Section>

      <Section n="4" title="Assumptions & Clarifications">
        <Bullets items={[
          "Record missing information and ambiguous requirements as explicit assumptions.",
          "Surface scope gaps early rather than absorbing them silently into the estimate.",
          "Draft clarification questions for the customer where the RFP is unclear.",
          "Document customer dependencies that the proposal will rely on.",
        ]} />
      </Section>

      <Section n="5" title="Risk Assessment">
        <Bullets items={[
          "Identify delivery risks tied to specific requirements or constraints.",
          "Flag scope ambiguity and integration complexity as early risks.",
          "Assess resource and timeline risks against the submission and delivery windows.",
          "Evaluate customer readiness as a risk that affects delivery confidence.",
        ]} />
      </Section>

      <Section n="6" title="Estimation Support">
        <Callout tone="amber">
          SmartBid provides estimation support, not final delivery commitments.
        </Callout>
        <Bullets items={[
          "Use effort estimation as a starting point validated by the delivery team.",
          "Plan resources and team profile against the estimated effort.",
          "Estimate timelines with explicit assumptions and dependencies.",
          "Model cost in ranges, not single fixed figures.",
        ]} />
      </Section>

      <Section n="7" title="Solution Recommendation Development">
        <Bullets items={[
          "Define a delivery approach that maps directly to the validated requirements.",
          "Outline the solution architecture at a proposal-appropriate level of detail.",
          "Propose a program structure and team composition that fits the scope.",
          "Frame a delivery strategy (phasing, methodology) that the team can stand behind.",
        ]} />
      </Section>

      <Section n="8" title="Proposal Development Guidance">
        <Bullets items={[
          "Lead with customer outcomes, not solution features.",
          "Articulate a clear value proposition tied to the customer's needs.",
          "Present the delivery approach and the differentiators that make this bid competitive.",
          "Show risk mitigation explicitly to build customer confidence.",
        ]} />
      </Section>

      <Section n="9" title="Executive Summary Guidance">
        <Bullets items={[
          "Customer needs — what the RFP is really asking for.",
          "Recommended approach — how the team will deliver it.",
          "Benefits — the outcomes the customer can expect.",
          "Risks — the key risks and how they are managed.",
          "Next steps — what happens after submission.",
        ]} />
      </Section>

      <Section n="10" title="Version Management">
        <Bullets items={[
          "Capture scope changes as the RFP and clarifications evolve.",
          "Track clarifications received from the customer against the proposal.",
          "Version estimate changes so pricing decisions remain traceable.",
          "Manage proposal revisions through governed versions, not overwrites.",
        ]} />
      </Section>

      <Section n="11" title="Client Communication">
        <Bullets items={[
          "Communicate in business language, not internal or tool terminology.",
          "Position the proposal around the customer's outcomes and priorities.",
          "Lead with value messaging supported by the recommended approach.",
          "Keep communication outcome-oriented throughout the pursuit.",
        ]} />
      </Section>

      <Section n="12" title="Ask PRIVA Usage">
        <Callout tone="blue">
          PRIVA supports interpretation and terminology — it does not make bid decisions.
        </Callout>
        <Bullets items={[
          "Use PRIVA to help interpret requirements and clarify intent.",
          "Lean on PRIVA for SAP, proposal, and industry terminology.",
          "Treat PRIVA as knowledge support — bid decisions stay with the consultant.",
        ]} />
      </Section>

      <Section n="13" title="Common Pitfalls">
        <Bullets items={[
          "Accepting AI extraction without consultant validation.",
          "Missing or undocumented assumptions.",
          "Ignoring integration effort hidden in the requirements.",
          "Committing to scope before clarification is resolved.",
          "Underestimating delivery complexity.",
        ]} />
      </Section>

      <Section n="14" title="Best Practices">
        <Bullets items={[
          "Validate all major requirements against the source RFP.",
          "Surface risks early so they can be mitigated in the proposal.",
          "Document every assumption explicitly.",
          "Align estimates and approach with delivery reality.",
          "Focus on customer outcomes throughout the bid.",
        ]} />
      </Section>

      {/* Final positioning */}
      <div className="bg-[#025979] text-white rounded-sm p-4 mt-6" data-testid="smartbid-playbook-positioning">
        <p className="text-[11px] uppercase tracking-wider opacity-80 font-semibold mb-2">From documents to proposal readiness</p>
        <div className="flex flex-wrap items-center gap-2 text-[11px] mb-3">
          <DarkPill>Documents</DarkPill><Arrow light />
          <DarkPill>Requirements</DarkPill><Arrow light />
          <DarkPill>Insights</DarkPill><Arrow light />
          <DarkPill>Estimates</DarkPill><Arrow light />
          <DarkPill>Recommendations</DarkPill><Arrow light />
          <DarkPill>Proposal Readiness</DarkPill>
        </div>
        <p className="text-[12px] leading-relaxed opacity-95">
          The objective is not simply to respond to an RFP. It is to build a
          realistic, defensible, and winnable proposal while maintaining delivery
          confidence and customer trust.
        </p>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Primitives (shared playbook template)
// ---------------------------------------------------------------------------
function Section({ n, title, children }) {
  if (!VALID.includes(n)) n = "•";
  return (
    <section
      data-testid={`smartbid-playbook-section-${n}`}
      className="mb-5 bg-white border border-[#E5E7EB] rounded-sm shadow-sm"
    >
      <div className="flex items-center gap-2 px-5 pt-4 pb-3 border-b border-[#E5E7EB] bg-[#F9FAFB]">
        <span className="inline-flex items-center justify-center min-w-5 h-5 px-1.5 rounded-sm bg-[#025979] text-white text-[10px] font-bold">
          {n}
        </span>
        <h2
          className="text-sm font-semibold text-[#111827] tracking-tight"
          style={{ fontFamily: "Chivo, sans-serif" }}
        >
          {title}
        </h2>
      </div>
      <div className="px-5 py-4 space-y-3">{children}</div>
    </section>
  );
}

function Bullets({ items }) {
  return (
    <ul className="space-y-2">
      {items.map((t, i) => (
        <li key={i} className="flex items-start gap-2 text-[12px] text-[#1F2937] leading-relaxed">
          <span className="mt-1.5 w-1.5 h-1.5 rounded-full bg-[#025979] shrink-0" />
          <span>{t}</span>
        </li>
      ))}
    </ul>
  );
}

const CALLOUT_TONES = {
  amber: "bg-[#FFFBEB] border-[#FCD34D]/60 text-[#78350F]",
  blue: "bg-[#EFF6FF] border-[#BFDBFE] text-[#1E3A8A]",
};

function Callout({ tone = "blue", children }) {
  return (
    <div className={`rounded-sm border px-3 py-2 text-[11px] leading-relaxed ${CALLOUT_TONES[tone]}`}>
      {children}
    </div>
  );
}

function Pill({ children }) {
  return (
    <span className="inline-flex items-center px-2 py-0.5 rounded-sm bg-[#E0F2FE] text-[#025979] border border-[#025979]/20 font-medium">
      {children}
    </span>
  );
}

function DarkPill({ children }) {
  return (
    <span className="inline-flex items-center px-2 py-0.5 rounded-sm bg-white/15 text-white border border-white/25 font-medium">
      {children}
    </span>
  );
}

function Arrow({ light = false }) {
  return <span className={light ? "text-white/60" : "text-[#9CA3AF]"}>→</span>;
}
